/**
 * Unified tenant-source client (the unified-tenant-source lifecycle): one backend contract for a casino
 * instance's tenant-scoped override of ANY curated data-source kind (a carousel or a game list),
 * replacing the two kind-specific route families (`./creative-api`'s carousel + game-list
 * publish/revert helpers) with a single shape both kinds share. See the plan at
 * `.claude/specs/2026-09-21-unified-tenant-source-PLAN.md`.
 */
import axios from 'axios';

import { creativesRequestConfig, creativesServiceBase, creativesServiceConfigured, type PreviewSource } from './creative-api';
import { sharedReads } from './shared-read';

/** Which data-source kind a tenant-source override targets. */
export type TenantSourceKind = 'carousel' | 'game_list';

/** Identifies one tenant-source override: a kind plus the underlying source's id (a carousel name
 *  or a game-list source id). */
export interface TenantSourceRef {
  kind: TenantSourceKind;
  source_id: string;
}

/** A tenant-source ref plus the ordered id list a write carries. */
export interface TenantSourceOrder extends TenantSourceRef {
  ids: string[];
}

/** Full state of one tenant-source override: the DRAFT (working order), PUBLISHED (live) and
 *  PREV_PUBLISHED (one-step-revert) orders, the source's own LIVE order (deployed data-source
 *  order, per-tenant if published else the template), whether a revert is currently possible, and
 *  whether the underlying source allows a tenant override at all (the publish precondition). */
export interface TenantSourceState extends TenantSourceRef {
  draft: string[];
  published: string[];
  prev_published: string[];
  live: string[];
  can_revert: boolean;
  allow_tenant_sources: boolean;
  /** The source is the whole game catalogue under another name (TalismanMax's "Todos"): not a list to arrange. */
  catalogue?: boolean;
}

/** What a publish returns: the newly published + live orders, and whether a revert is now possible. */
export interface TenantSourcePublished {
  published: string[];
  live: string[];
  can_revert: boolean;
}

/** What a revert returns: the restored draft/published/prev_published/live orders. */
export interface TenantSourceReverted {
  draft: string[];
  published: string[];
  prev_published: string[];
  live: string[];
  can_revert: boolean;
}

/** What a tenant-source call came to when it did not produce a value: `unavailable` (this environment
 *  has no tenant-source backend, its route answers 404, or no creatives service at all), `refused` (the
 *  service declined with a reason the owner can act on, its message is meant to be shown as is),
 *  `failed` (anything else: network error, 5xx, an unexpected shape). */
export type TenantSourceFailure = { ok: false; reason: 'unavailable' | 'refused' | 'failed'; message?: string };

/** Result of any tenant-source call: either the parsed value, or a structured failure. */
export type TenantSourceResult<T> = { ok: true; value: T } | TenantSourceFailure;

/** Filter a response field down to a plain string id list (drops anything else: numbers, null,
 *  objects), applied to every id-list field of a response. */
export const idList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((id): id is string => typeof id === 'string') : [];

/** Map a tenant-source response onto a {@link TenantSourceResult}, given how to parse a 2xx body.
 *  Pure, so the status rules are testable without a network: a 2xx body is parsed; a 404 is a route not deployed in this environment
 *  (the handlers themselves never answer one); a 400 carrying `{error}` is the service's own
 *  refusal (shown as is); anything else is `failed`. */
export const tenantSourceResult = <T>(
  status: number,
  data: unknown,
  parse: (body: Record<string, unknown>) => T,
): TenantSourceResult<T> => {
  const body = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  if (status >= 200 && status < 300) return { ok: true, value: parse(body) };
  if (status === 404) return { ok: false, reason: 'unavailable' };
  const message = typeof body.error === 'string' && body.error !== '' ? body.error : undefined;
  if (status === 400 && message) return { ok: false, reason: 'refused', message };
  return { ok: false, reason: 'failed', message };
};

const isTenantSourceKind = (value: unknown): value is TenantSourceKind => value === 'carousel' || value === 'game_list';

/** Parse a `GET .../tenant-source` body into {@link TenantSourceState}. The live backend always
 *  sends all 8 keys; this still defaults a missing order list to `[]`, a missing flag to `false`,
 *  a missing/invalid `kind` to `'game_list'`, and a missing `source_id` to `''`, so a partial or
 *  stale response degrades to an empty-but-typed state rather than throwing. */
export const parseTenantSourceState = (body: Record<string, unknown>): TenantSourceState => ({
  kind: isTenantSourceKind(body.kind) ? body.kind : 'game_list',
  source_id: typeof body.source_id === 'string' ? body.source_id : '',
  draft: idList(body.draft),
  published: idList(body.published),
  prev_published: idList(body.prev_published),
  live: idList(body.live),
  can_revert: body.can_revert === true,
  allow_tenant_sources: body.allow_tenant_sources === true,
  ...(body.catalogue === true ? { catalogue: true } : {}),
});

const tenantSourcePath = (casinoInstanceId: string): string =>
  `${creativesServiceBase()}/creatives/casino-instance/${encodeURIComponent(casinoInstanceId)}/tenant-source`;

/** Read one tenant-source override's full state. */
export const getTenantSourceState = async (
  casinoInstanceId: string,
  ref: TenantSourceRef,
  signal?: AbortSignal,
): Promise<TenantSourceResult<TenantSourceState>> => {
  if (!creativesServiceConfigured()) return { ok: false, reason: 'unavailable' };
  try {
    const response = await axios.get(
      `${tenantSourcePath(casinoInstanceId)}?kind=${encodeURIComponent(ref.kind)}&source_id=${encodeURIComponent(ref.source_id)}`,
      creativesRequestConfig(signal),
    );
    return tenantSourceResult(response.status, response.data, parseTenantSourceState);
  } catch {
    return { ok: false, reason: 'failed' };
  }
};

// The state reads every reader shares, per brand, kind and source (see `shared-read`).
const stateReads = sharedReads<TenantSourceState>();
const stateKey = (casinoInstanceId: string, ref: TenantSourceRef): string =>
  `${casinoInstanceId}|${ref.kind}|${ref.source_id}`;

/** `getTenantSourceState`, shared by every reader of the override for `SHARED_READ_MS`: the fresh answer, else the read
 *  in flight, else one new read. It carries no reader's signal, so one reader leaving never cancels it for another; a
 *  reader checks its own abort once it has the answer. Every write of the override below drops it before it is sent
 *  and again once it settles: a read answered meanwhile may hold the state from before the write. */
export const getTenantSourceStateShared = (
  casinoInstanceId: string,
  ref: TenantSourceRef,
): Promise<TenantSourceResult<TenantSourceState>> =>
  stateReads.get(stateKey(casinoInstanceId, ref), () => getTenantSourceState(casinoInstanceId, ref));

/** Forget the shared read of one override's state: the next reader asks the service again. A read in flight now still
 *  answers its reader, but is not kept. */
export const dropTenantSourceState = (casinoInstanceId: string, ref: TenantSourceRef): void => {
  const dropped = stateKey(casinoInstanceId, ref);
  stateReads.drop((key) => key === dropped);
};

/** Autosave the working DRAFT order for one tenant-source override (published is untouched). */
export const saveTenantSourceDraft = async (
  casinoInstanceId: string,
  order: TenantSourceOrder,
  signal?: AbortSignal,
): Promise<TenantSourceResult<{ ids: string[] }>> => {
  if (!creativesServiceConfigured()) return { ok: false, reason: 'unavailable' };
  dropTenantSourceState(casinoInstanceId, order);
  try {
    const response = await axios.put(
      `${tenantSourcePath(casinoInstanceId)}/draft`,
      { kind: order.kind, source_id: order.source_id, ids: order.ids },
      creativesRequestConfig(signal),
    );
    return tenantSourceResult(response.status, response.data, (body) => ({ ids: idList(body.ids) }));
  } catch {
    return { ok: false, reason: 'failed' };
  } finally {
    dropTenantSourceState(casinoInstanceId, order);
  }
};

/** Publish the owner's order for one tenant-source override to the live site. */
export const publishTenantSource = async (
  casinoInstanceId: string,
  order: TenantSourceOrder,
  signal?: AbortSignal,
): Promise<TenantSourceResult<TenantSourcePublished>> => {
  if (!creativesServiceConfigured()) return { ok: false, reason: 'unavailable' };
  dropTenantSourceState(casinoInstanceId, order);
  try {
    const response = await axios.post(
      `${tenantSourcePath(casinoInstanceId)}/publish`,
      { kind: order.kind, source_id: order.source_id, ids: order.ids },
      creativesRequestConfig(signal),
    );
    return tenantSourceResult(response.status, response.data, (body) => ({
      published: idList(body.published),
      live: idList(body.live),
      can_revert: body.can_revert === true,
    }));
  } catch {
    return { ok: false, reason: 'failed' };
  } finally {
    dropTenantSourceState(casinoInstanceId, order);
  }
};

/** Drop the brand's own order for one tenant-source override, restoring the previously published
 *  order as the live (and draft) order (owners can always undo a publish). */
export const revertTenantSource = async (
  casinoInstanceId: string,
  ref: TenantSourceRef,
  signal?: AbortSignal,
): Promise<TenantSourceResult<TenantSourceReverted>> => {
  if (!creativesServiceConfigured()) return { ok: false, reason: 'unavailable' };
  dropTenantSourceState(casinoInstanceId, ref);
  try {
    const response = await axios.post(
      `${tenantSourcePath(casinoInstanceId)}/revert`,
      { kind: ref.kind, source_id: ref.source_id },
      creativesRequestConfig(signal),
    );
    return tenantSourceResult(response.status, response.data, (body) => ({
      draft: idList(body.draft),
      published: idList(body.published),
      prev_published: idList(body.prev_published),
      live: idList(body.live),
      can_revert: body.can_revert === true,
    }));
  } catch {
    return { ok: false, reason: 'failed' };
  } finally {
    dropTenantSourceState(casinoInstanceId, ref);
  }
};

/** Fetch the site-faithful preview payload for a tenant-source's in-flight (not-yet-published)
 *  order (carousel kinds only), for the live-preview overlay. Returns
 *  `null` on any non-2xx response, a missing `ds_id`/`source`, or a network failure: the caller
 *  keeps whatever preview it already has rather than treating this as a hard error. */
export const previewTenantSource = async (
  casinoInstanceId: string,
  order: TenantSourceOrder,
  signal?: AbortSignal,
): Promise<PreviewSource | null> => {
  if (!creativesServiceConfigured()) return null;
  try {
    const response = await axios.post(
      `${tenantSourcePath(casinoInstanceId)}/preview`,
      { kind: order.kind, source_id: order.source_id, ids: order.ids },
      creativesRequestConfig(signal),
    );
    if (response.status < 200 || response.status >= 300) return null;
    const data = response.data as { ds_id?: unknown; source?: unknown } | undefined;
    const dsId = data?.ds_id;
    const source = data?.source;
    if (typeof dsId !== 'string' || !source || typeof source !== 'object') return null;
    return { ds_id: dsId, source: source as PreviewSource['source'] };
  } catch {
    return null;
  }
};
